import React, { useCallback, useRef, useState, useEffect, useLayoutEffect } from 'react';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { EditorPanel } from '../panels/EditorPanel';
import HomeLauncherPanel from '../panels/HomeLauncherPanel';   // Feb-2026
import { ChaptersPanel } from '../panels/ChaptersPanel';
import { ModesPanel } from '../panels/ModesPanel';
import { VariablesPanel } from '../panels/VariablesPanel';
import { CharacterBiblePanel } from '../panels/CharacterBiblePanel';
import AdvancedCharacterIdentityPanel from '../panels/AdvancedCharacterIdentityPanel';
import NotebookPanel from '../panels/NotebookPanel';
import { ArcVisualizerPanel } from '../panels/ArcVisualizerPanel';
import { ClaudeChatPanel } from '../panels/ClaudeChatPanel';
import { AnalysisPanel } from '../panels/AnalysisPanel';
import { MarketingPanel } from '../panels/MarketingPanel';
import EditorialServicesPanel from '../panels/editorial/EditorialServicesPanel';  // Feb-2026 Phase 2
import { MyBookshelfCarousel } from '../panels/MyBookshelfCarousel';   // iter-shelf1
import DeviationInboxPanel from '../panels/DeviationInboxPanel';        // Craft Standards inbox
import { PlotExtractionPanel } from '../panels/PlotExtractionPanel';   // Plot Blueprint panel
import { WorldBuilderPanel } from '../panels/WorldBuilderPanel';         // World Builder panel
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Layers, SlidersHorizontal, ChevronLeft, ChevronRight, BookText, Users, NotebookPen, X, AlertTriangle } from 'lucide-react';

// iter-41/42 — Side-column "expand to half" sizes (% of workspace width).
// iter-72b — Bumped the resting maxSize so users can drag the mullion
// (the vertical handle between the left column and the editor) all the
// way out to reveal the full Character Bible without first clicking
// the "expand to half" caret. `SIDE_MAX_PCT` remains the ceiling for
// the maximize toggle.
const LEFT_NORMAL_PCT  = 18;
const LEFT_DRAG_MAX    = 55;   // drag-only ceiling when Bible column is present
const RIGHT_NORMAL_PCT = 26;
const RIGHT_DRAG_MAX   = 55;   // drag-only ceiling when right column is present
const SIDE_MAX_PCT     = 50;   // half the workspace per user request

const normalizedPanelSizes = (weights) => {
  const active = Object.entries(weights).filter(([, weight]) => weight > 0);
  const total = active.reduce((sum, [, weight]) => sum + weight, 0) || 1;
  return Object.fromEntries(active.map(([key, weight]) => [key, (weight / total) * 100]));
};

// panel key → visible key map for loom:open-panel event
const PANEL_KEY_MAP = {
  variables:  'variables',
  bible:      'bible',
  editor:     'editor',
  analysis:   'analysis',
  chat:       'chat',
  arc:        'arc',
  marketing:  'marketing',
  editorial:  'editorial',
  chapters:   'chapters',
  bookshelf:  'bookshelf',
  deviation:  'deviation',
  plotex:     'plotex',
  worldex:    'worldex',
};

export function Workspace({ visible, togglePanel, simpleMode, projectId, onGoToChapter }) {
  // Wire loom:open-panel events from WorkflowProgressStrip
  useEffect(() => {
    const handler = (e) => {
      const key = PANEL_KEY_MAP[e.detail];
      if (key) togglePanel(key, true);   // always open (never close) via event
    };
    window.addEventListener('loom:open-panel', handler);
    return () => window.removeEventListener('loom:open-panel', handler);
  }, [visible, togglePanel]);
  const hasLeft   = visible.chapters || visible.bible || visible.advChar;
  const hasCenter = visible.editor || visible.modes || visible.variables || visible.home;
  const hasRight  = visible.chat || visible.arc || visible.analysis || visible.marketing || visible.editorial || visible.bookshelf || visible.deviation || visible.plotex || visible.worldex;
  const outerSizes = normalizedPanelSizes({
    left: hasLeft ? LEFT_NORMAL_PCT : 0,
    center: hasCenter ? 56 : 0,
    right: hasRight ? RIGHT_NORMAL_PCT : 0,
  });

  // Imperative refs so we can resize() each side column when the
  // Character Bible / Chat / Arc / Analysis maximize toggle is clicked.
  const leftPanelRef  = useRef(null);
  const rightPanelRef = useRef(null);
  const [bibleMax, setBibleMax]       = useState(false);
  const [rightMax, setRightMax]       = useState(false);
  const [notebookMax, setNotebookMax] = useState(false);
  // iter-beta1 — Collapsible left side rail (Chapters / Character Bible /
  // Notebook). Collapsing shrinks it to a thin icon-only strip instead of
  // hiding it outright, so the writer can see what's tucked away and
  // re-expand with one click.
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const toggleLeftCollapsed = useCallback(() => {
    if (leftCollapsed) leftPanelRef.current?.expand();
    else leftPanelRef.current?.collapse();
  }, [leftCollapsed]);

  const toggleBibleMax = useCallback(() => {
    setBibleMax((prev) => {
      const next = !prev;
      requestAnimationFrame(() => {
        leftPanelRef.current?.resize(next ? SIDE_MAX_PCT : LEFT_NORMAL_PCT);
      });
      return next;
    });
  }, []);

  const toggleNotebookMax = useCallback(() => {
    setNotebookMax((prev) => {
      const next = !prev;
      requestAnimationFrame(() => {
        leftPanelRef.current?.resize(next ? SIDE_MAX_PCT : LEFT_NORMAL_PCT);
      });
      return next;
    });
  }, []);

  const toggleRightMax = useCallback(() => {
    setRightMax((prev) => {
      const next = !prev;
      requestAnimationFrame(() => {
        rightPanelRef.current?.resize(next ? SIDE_MAX_PCT : RIGHT_NORMAL_PCT);
      });
      return next;
    });
  }, []);

  // Reset the maximized flags when the corresponding column is hidden so
  // the next open starts at normal width.
  React.useEffect(() => {
    if (!visible.bible && bibleMax) setBibleMax(false);
  }, [visible.bible, bibleMax]);
  React.useEffect(() => {
    if (!hasRight && rightMax) setRightMax(false);
  }, [hasRight, rightMax]);

  // ── Dynamic layout: when a panel column transitions hidden→visible, give
  // it the correct default share and push the editor to make room. ──────────
  const prevHasLeft  = useRef(hasLeft);
  const prevHasRight = useRef(hasRight);
  useLayoutEffect(() => {
    if (!prevHasLeft.current && hasLeft) {
      requestAnimationFrame(() => {
        leftPanelRef.current?.resize(bibleMax || notebookMax ? SIDE_MAX_PCT : LEFT_NORMAL_PCT);
      });
    }
    prevHasLeft.current = hasLeft;
  }, [hasLeft, bibleMax, notebookMax]);
  useLayoutEffect(() => {
    if (!prevHasRight.current && hasRight) {
      requestAnimationFrame(() => {
        rightPanelRef.current?.resize(rightMax ? SIDE_MAX_PCT : RIGHT_NORMAL_PCT);
      });
    }
    prevHasRight.current = hasRight;
  }, [hasRight, rightMax]);

  return (
    <div className="relative flex-1 min-w-0 bg-background border border-border rounded-sm overflow-hidden">
      <ResizablePanelGroup direction="horizontal" className="h-full w-full">
        {hasLeft && (
          <>
            <ResizablePanel
              ref={leftPanelRef}
              defaultSize={outerSizes.left}
              minSize={14}
              maxSize={bibleMax ? SIDE_MAX_PCT : LEFT_DRAG_MAX}
              collapsible
              collapsedSize={4}
              onCollapse={() => setLeftCollapsed(true)}
              onExpand={() => setLeftCollapsed(false)}
              className="flex flex-col"
            >
              <LeftColumn visible={visible} togglePanel={togglePanel}
                           bibleMax={bibleMax} onToggleBibleMax={toggleBibleMax}
                           notebookMax={notebookMax} onToggleNotebookMax={toggleNotebookMax}
                           collapsed={leftCollapsed} onToggleCollapsed={toggleLeftCollapsed} />
            </ResizablePanel>
            <ResizableHandle withHandle data-testid="workspace-handle-left" />
          </>
        )}

        {hasCenter && (
          <ResizablePanel defaultSize={outerSizes.center} minSize={30} className="flex flex-col">
            <CenterColumn visible={visible} togglePanel={togglePanel} simpleMode={simpleMode} />
          </ResizablePanel>
        )}

        {hasRight && (
          <>
            <ResizableHandle withHandle data-testid="workspace-handle-right" />
            <ResizablePanel
              ref={rightPanelRef}
              defaultSize={outerSizes.right}
              minSize={18}
              maxSize={rightMax ? SIDE_MAX_PCT : RIGHT_DRAG_MAX}
            >
              <RightColumn visible={visible} togglePanel={togglePanel}
                            rightMax={rightMax} onToggleRightMax={toggleRightMax} simpleMode={simpleMode}
                            projectId={projectId} onGoToChapter={onGoToChapter} />
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>
    </div>
  );
}

function LeftColumn({ visible, togglePanel, bibleMax, onToggleBibleMax, notebookMax, onToggleNotebookMax, collapsed, onToggleCollapsed }) {
  /* iter55f58 — LeftColumn can now stack up to 3 panels: Chapters,
   * Character Bible, and (for Advanced Modeling users) the split-out
   * Character Identity / archetype editor. Sizes rebalance depending
   * on which are visible.
   *
   * iter55f96 — Add explicit `id` + `order` on every ResizablePanel so
   * `react-resizable-panels` doesn't reverse the drag direction when a
   * panel is conditionally hidden. Without stable ids the library
   * re-indexes panels by mount order, and toggling Chapters off/on
   * caused the internal top-panel to become the bottom-panel in its
   * bookkeeping — dragging the Bible's top edge grew the wrong side.
   */
  const advChar = !!visible.advChar;
  const sizes = normalizedPanelSizes({
    chapters: visible.chapters ? (advChar ? 40 : 55) : 0,
    bible: visible.bible ? (advChar ? 35 : 45) : 0,
    advChar: advChar ? 25 : 0,
    notebook: visible.notebook ? 22 : 0,
  });

  // iter-beta1 — Collapsed state renders a thin icon-only strip (like
  // VSCode's activity bar) instead of the full stacked panels, with a
  // chevron to expand and one icon per currently-visible sub-panel that
  // also expands when clicked.
  if (collapsed) {
    const rail = [
      visible.chapters && { key: 'chapters', icon: BookText,  label: 'Chapters' },
      visible.bible     && { key: 'bible',   icon: Users,     label: 'Character Bible' },
      advChar           && { key: 'advChar', icon: Users,     label: 'Character Identity' },
      visible.notebook  && { key: 'notebook',icon: NotebookPen,label: 'Notebook' },
    ].filter(Boolean);
    return (
      <div className="flex h-full w-full flex-col items-center gap-1 bg-panel-header py-1.5"
           data-testid="side-rail-collapsed">
        <button
          type="button"
          onClick={onToggleCollapsed}
          title="Expand side rail"
          data-testid="side-rail-expand-btn"
          className="flex h-7 w-7 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
        <div className="mt-1 flex flex-col gap-0.5">
          {rail.map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              type="button"
              onClick={onToggleCollapsed}
              title={label}
              data-testid={`side-rail-icon-${key}`}
              className="flex h-8 w-8 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-primary"
            >
              <Icon className="h-4 w-4" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-5 shrink-0 items-center justify-end border-b border-panel-border bg-panel-header px-1">
        <button
          type="button"
          onClick={onToggleCollapsed}
          title="Collapse side rail"
          data-testid="side-rail-collapse-btn"
          className="flex h-4 w-4 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" />
        </button>
      </div>
      <div className="flex-1 min-h-0">
        <ResizablePanelGroup direction="vertical" id="left-column-group">
          {visible.chapters && (
            <ResizablePanel id="left-chapters" order={1} defaultSize={sizes.chapters} minSize={18}>
              <ChaptersPanel onClose={() => togglePanel('chapters')} />
            </ResizablePanel>
          )}
          {visible.chapters && visible.bible && <ResizableHandle withHandle id="left-handle-chapters-bible" />}
          {visible.bible && (
            <ResizablePanel id="left-bible" order={2} defaultSize={sizes.bible} minSize={18}>
              <CharacterBiblePanel
                onClose={() => togglePanel('bible')}
                maximized={bibleMax}
                onMaximize={onToggleBibleMax}
              />
            </ResizablePanel>
          )}
          {visible.bible && advChar && <ResizableHandle withHandle id="left-handle-bible-adv" />}
          {advChar && (
            <ResizablePanel id="left-advchar" order={3} defaultSize={sizes.advChar} minSize={18}>
              <AdvancedCharacterIdentityPanel onClose={() => togglePanel('advChar')} />
            </ResizablePanel>
          )}
          {(visible.chapters || visible.bible || advChar) && visible.notebook && <ResizableHandle withHandle id="left-handle-notebook" />}
          {visible.notebook && (
            <ResizablePanel id="left-notebook" order={4} defaultSize={sizes.notebook} minSize={16}>
              {/* Notebook now rendered as center overlay — nothing here */}
            </ResizablePanel>
          )}
        </ResizablePanelGroup>
      </div>
    </div>
  );
}

function CenterColumn({ visible, togglePanel, simpleMode }) {
  const showTabs = visible.modes || visible.variables;
  const sizes = normalizedPanelSizes({ editor: visible.editor ? 62 : 0, tabs: showTabs ? 38 : 0 });
  return (
    <div className="relative flex flex-1 flex-col overflow-hidden">
    <ResizablePanelGroup direction="vertical" id="center-column-group">
      {visible.home && !visible.editor && (
        <ResizablePanel id="center-home" order={0} defaultSize={100} minSize={20}>
          <HomeLauncherPanel togglePanel={togglePanel} />
        </ResizablePanel>
      )}
      {visible.editor && (
        <ResizablePanel id="center-editor" order={1} defaultSize={sizes.editor} minSize={20}>
          <EditorPanel onClose={() => togglePanel('editor')} simpleMode={simpleMode} />
        </ResizablePanel>
      )}
      {visible.editor && showTabs && <ResizableHandle withHandle id="center-handle" />}
      {showTabs && (
        <ResizablePanel id="center-tabs" order={2} defaultSize={sizes.tabs} minSize={20}>
          <CenterTabs visible={visible} togglePanel={togglePanel} />
        </ResizablePanel>
      )}
    </ResizablePanelGroup>

    {/* ── Writer's Notebook full-editor overlay ──────────────────────────── *
     *  When the notebook is toggled, it floats above the entire writing    *
     *  area (absolute, inset-0) so the author can jot notes without        *
     *  leaving the editor context.                                          */}
    {visible.notebook && (
      <div
        className="absolute inset-0 z-30 animate-in fade-in duration-200"
        data-testid="notebook-overlay"
      >
        <NotebookPanel
          onClose={() => togglePanel('notebook')}
          maximized
          onMaximize={() => {}}
        />
      </div>
    )}
    </div>
  );
}

function RightColumn({ visible, togglePanel, rightMax, onToggleRightMax, simpleMode, projectId, onGoToChapter }) {
  const sizes = normalizedPanelSizes({
    chat:       visible.chat      ? 42 : 0,
    arc:        visible.arc       ? 32 : 0,
    analysis:   visible.analysis  ? 26 : 0,
    marketing:  visible.marketing ? 30 : 0,
    editorial:  visible.editorial ? 34 : 0,
    bookshelf:  visible.bookshelf ? 34 : 0,
    deviation:  visible.deviation ? 32 : 0,
    plotex:     visible.plotex    ? 30 : 0,
    worldex:    visible.worldex   ? 30 : 0,
  });
  return (
    <ResizablePanelGroup direction="vertical" id="right-column-group">
      {visible.chat && (
        <ResizablePanel id="right-chat" order={1} defaultSize={sizes.chat} minSize={20}>
          <ClaudeChatPanel onClose={() => togglePanel('chat')}
                            maximized={rightMax} onMaximize={onToggleRightMax} simpleMode={simpleMode} />
        </ResizablePanel>
      )}
      {visible.chat && visible.arc && <ResizableHandle withHandle id="right-handle-chat-arc" />}
      {visible.arc && (
        <ResizablePanel id="right-arc" order={2} defaultSize={sizes.arc} minSize={20}>
          <ArcVisualizerPanel onClose={() => togglePanel('arc')}
                                maximized={rightMax} onMaximize={onToggleRightMax} />
        </ResizablePanel>
      )}
      {visible.arc && visible.analysis && <ResizableHandle withHandle id="right-handle-arc-analysis" />}
      {visible.analysis && (
        <ResizablePanel id="right-analysis" order={3} defaultSize={sizes.analysis} minSize={18}>
          <AnalysisPanel onClose={() => togglePanel('analysis')}
                          maximized={rightMax} onMaximize={onToggleRightMax} />
        </ResizablePanel>
      )}
      {visible.analysis && visible.marketing && <ResizableHandle withHandle id="right-handle-analysis-marketing" />}
      {visible.marketing && (
        <ResizablePanel id="right-marketing" order={4} defaultSize={sizes.marketing} minSize={20}>
          <MarketingPanel onClose={() => togglePanel('marketing')}
                          maximized={rightMax} onMaximize={onToggleRightMax} />
        </ResizablePanel>
      )}
      {visible.marketing && visible.editorial && <ResizableHandle withHandle id="right-handle-marketing-editorial" />}
      {visible.editorial && (
        <ResizablePanel id="right-editorial" order={5} defaultSize={sizes.editorial} minSize={22}>
          <EditorialServicesPanel onClose={() => togglePanel('editorial')} />
        </ResizablePanel>
      )}
      {(visible.chat || visible.arc || visible.analysis || visible.marketing || visible.editorial) && visible.bookshelf && <ResizableHandle withHandle id="right-handle-editorial-bookshelf" />}
      {visible.bookshelf && (
        <ResizablePanel id="right-bookshelf" order={6} defaultSize={sizes.bookshelf} minSize={22}>
          <MyBookshelfCarousel onClose={() => togglePanel('bookshelf')}
                                maximized={rightMax} onMaximize={onToggleRightMax} />
        </ResizablePanel>
      )}
      {(visible.chat || visible.arc || visible.analysis || visible.marketing || visible.editorial || visible.bookshelf) && visible.deviation && <ResizableHandle withHandle id="right-handle-bookshelf-deviation" />}
      {visible.deviation && (
        <ResizablePanel id="right-deviation" order={7} defaultSize={sizes.deviation} minSize={22}>
          <div className="flex h-full flex-col bg-white">
            <div className="flex items-center justify-between border-b border-neutral-200 bg-neutral-50 px-3 py-2">
              <div className="flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                <span className="text-[12px] font-semibold text-neutral-700">Craft Deviations</span>
              </div>
              <button onClick={() => togglePanel('deviation')}
                      className="rounded p-0.5 text-neutral-400 hover:bg-neutral-200"
                      data-testid="deviation-panel-close">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <DeviationInboxPanel
                projectId={projectId}
                onGoToChapter={onGoToChapter}
              />
            </div>
          </div>
        </ResizablePanel>
      )}
      {(visible.chat || visible.arc || visible.analysis || visible.marketing || visible.editorial || visible.bookshelf || visible.deviation) && visible.plotex && <ResizableHandle withHandle id="right-handle-deviation-plotex" />}
      {visible.plotex && (
        <ResizablePanel id="right-plotex" order={8} defaultSize={sizes.plotex} minSize={22}>
          <PlotExtractionPanel onClose={() => togglePanel('plotex')}
                               maximized={rightMax} onMaximize={onToggleRightMax} />
        </ResizablePanel>
      )}
      {(visible.chat || visible.arc || visible.analysis || visible.marketing || visible.editorial || visible.bookshelf || visible.deviation || visible.plotex) && visible.worldex && <ResizableHandle withHandle id="right-handle-plotex-worldex" />}
      {visible.worldex && (
        <ResizablePanel id="right-worldex" order={9} defaultSize={sizes.worldex} minSize={22}>
          <WorldBuilderPanel onClose={() => togglePanel('worldex')}
                             maximized={rightMax} onMaximize={onToggleRightMax} />
        </ResizablePanel>
      )}
    </ResizablePanelGroup>
  );
}

function CenterTabs({ visible, togglePanel }) {
  const defaultTab = visible.modes ? 'modes' : 'variables';
  return (
    <div className="h-full panel-bg border-t border-panel-border">
      <Tabs defaultValue={defaultTab} className="flex h-full flex-col">
        <div className="flex items-center justify-between border-b border-panel-border bg-panel-header px-2">
          <TabsList className="h-8 bg-transparent gap-1 p-0">
            {visible.modes && (
              <TabsTrigger value="modes" className="h-7 gap-1.5 px-2.5 text-[11.5px] data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">
                <Layers className="h-3.5 w-3.5" /> Editing Modes
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); togglePanel('modes'); }}
                  className="ml-1 flex h-4 w-4 items-center justify-center rounded hover:bg-destructive hover:text-destructive-foreground text-muted-foreground"
                  title="Close Editing Modes"
                  data-testid="close-modes-tab"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </TabsTrigger>
            )}
            {visible.variables && (
              <TabsTrigger value="variables" className="h-7 gap-1.5 px-2.5 text-[11.5px] data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">
                <SlidersHorizontal className="h-3.5 w-3.5" /> Editable Variables
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); togglePanel('variables'); }}
                  className="ml-1 flex h-4 w-4 items-center justify-center rounded hover:bg-destructive hover:text-destructive-foreground text-muted-foreground"
                  title="Close Editable Variables"
                  data-testid="close-variables-tab"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </TabsTrigger>
            )}
          </TabsList>
          <SyncStatus />
        </div>
        {visible.modes && (
          <TabsContent value="modes" className="flex-1 m-0 overflow-hidden">
            <ModesPanel onClose={() => togglePanel('modes')} embedded />
          </TabsContent>
        )}
        {visible.variables && (
          <TabsContent value="variables" className="flex-1 m-0 overflow-hidden">
            <VariablesPanel onClose={() => togglePanel('variables')} embedded />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function SyncStatus() {
  const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return (
    <div className="flex items-center gap-1 text-[10.5px] text-muted-foreground">
      <span className="inline-flex h-1.5 w-1.5 rounded-full bg-success" />
      <span>Sync’d with Claude {time}</span>
    </div>
  );
}
